/*!
 * Copyright (C) Microsoft Corporation. All rights reserved.
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { v4 as uuid } from 'uuid';
import { languageName } from './PowerFxSyntax';
var Methods;
(function (Methods) {
    Methods["didOpen"] = "textDocument/didOpen";
    Methods["didChange"] = "textDocument/didChange";
    Methods["completion"] = "textDocument/completion";
    Methods["signatureHelp"] = "textDocument/signatureHelp";
    Methods["publishDiagnostics"] = "textDocument/publishDiagnostics";
    Methods["codeActions"] = "textDocument/codeAction";
    Methods["commandExecuted"] = "$/commandExecuted";
    Methods["publishTokens"] = "$/publishTokens";
    Methods["initialFixup"] = "$/initialFixup";
})(Methods || (Methods = {}));
var ApiTimeoutInMilliseconds = 20000;
/**
 * Token result type mapped to rules index in PowerFxTheme.ts
 */
export var TokenResultType;
(function (TokenResultType) {
    TokenResultType[TokenResultType["Boolean"] = 0] = "Boolean";
    TokenResultType[TokenResultType["Keyword"] = 1] = "Keyword";
    TokenResultType[TokenResultType["Function"] = 2] = "Function";
    TokenResultType[TokenResultType["Number"] = 3] = "Number";
    TokenResultType[TokenResultType["Operator"] = 4] = "Operator";
    TokenResultType[TokenResultType["HostSymbol"] = 5] = "HostSymbol";
    TokenResultType[TokenResultType["Variable"] = 6] = "Variable";
})(TokenResultType || (TokenResultType = {}));
export var GetTokensFlags;
(function (GetTokensFlags) {
    GetTokensFlags[GetTokensFlags["None"] = 0] = "None";
    GetTokensFlags[GetTokensFlags["UsedInExpression"] = 1] = "UsedInExpression";
    GetTokensFlags[GetTokensFlags["AllFunctions"] = 2] = "AllFunctions";
})(GetTokensFlags || (GetTokensFlags = {}));
var PowerFxLanguageClient = /** @class */ (function () {
    function PowerFxLanguageClient(_getDocumentUriAsync, _sendToLanguageServerAsync, _handleDiagnosticsNotification, _handleTokensNotification) {
        var _this = this;
        this._getDocumentUriAsync = _getDocumentUriAsync;
        this._sendToLanguageServerAsync = _sendToLanguageServerAsync;
        this._handleDiagnosticsNotification = _handleDiagnosticsNotification;
        this._handleTokensNotification = _handleTokensNotification;
        this._pendingPromises = [];
        this._sendNotificationAsync = function (payload) { return __awaiter(_this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                return [2 /*return*/, this._sendToLanguageServerAsync(payload)];
            });
        }); };
        this._sendRequestAsync = function (id, payload) { return __awaiter(_this, void 0, void 0, function () {
            var result, receiveMessageResolve, initializePromise, timeout_1, waitTimeout, _a, index;
            var _this = this;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        result = { success: false };
                        initializePromise = new Promise(function (resolve) {
                            receiveMessageResolve = resolve;
                            _this._pendingPromises.push({
                                id: id,
                                resolve: receiveMessageResolve,
                            });
                        });
                        _b.label = 1;
                    case 1:
                        _b.trys.push([1, , 4, 5]);
                        return [4 /*yield*/, this._sendToLanguageServerAsync(payload)];
                    case 2:
                        _b.sent();
                        timeout_1 = false;
                        waitTimeout = setTimeout(function () {
                            timeout_1 = true;
                            receiveMessageResolve();
                        }, ApiTimeoutInMilliseconds);
                        _a = result;
                        return [4 /*yield*/, initializePromise];
                    case 3:
                        _a.response = _b.sent();
                        clearTimeout(waitTimeout);
                        result.success = !timeout_1;
                        return [3 /*break*/, 5];
                    case 4:
                        index = this._pendingPromises.findIndex(function (item) { return item.id === id; });
                        if (index !== -1) {
                            this._pendingPromises.splice(index, 1);
                        }
                        return [7 /*endfinally*/];
                    case 5: return [2 /*return*/, result];
                }
            });
        }); };
    }
    PowerFxLanguageClient.prototype.notifyDidOpenAsync = function (text) {
        return __awaiter(this, void 0, void 0, function () {
            var uri, documentUrl, params, body;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this._getDocumentUriAsync()];
                    case 1:
                        uri = _a.sent();
                        documentUrl = new URL(uri);
                        documentUrl.searchParams.set('getTokensFlags', (GetTokensFlags.AllFunctions + GetTokensFlags.UsedInExpression).toString());
                        params = {
                            textDocument: {
                                uri: documentUrl.href,
                                languageId: languageName,
                                version: 0,
                                text: text,
                            },
                        };
                        body = {
                            jsonrpc: '2.0',
                            method: Methods.didOpen,
                            params: params,
                        };
                        return [4 /*yield*/, this._sendNotificationAsync(JSON.stringify(body))];
                    case 2:
                        _a.sent();
                        return [2 /*return*/];
                }
            });
        });
    };
    PowerFxLanguageClient.prototype.notifyDidChangeAsync = function (text, version) {
        return __awaiter(this, void 0, void 0, function () {
            var uri, documentUrl, params, body;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this._getDocumentUriAsync()];
                    case 1:
                        uri = _a.sent();
                        documentUrl = new URL(uri);
                        documentUrl.searchParams.set('getTokensFlags', GetTokensFlags.UsedInExpression.toString());
                        params = {
                            textDocument: {
                                uri: documentUrl.href,
                                version: version,
                            },
                            contentChanges: [{ text: text }],
                        };
                        body = {
                            jsonrpc: '2.0',
                            method: Methods.didChange,
                            params: params,
                        };
                        return [4 /*yield*/, this._sendNotificationAsync(JSON.stringify(body))];
                    case 2:
                        _a.sent();
                        return [2 /*return*/];
                }
            });
        });
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    PowerFxLanguageClient.prototype.notifyCommandExecutedAsync = function (command, args) {
        return __awaiter(this, void 0, void 0, function () {
            var uri, documentUrl, _a, id, params, body;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0: return [4 /*yield*/, this._getDocumentUriAsync()];
                    case 1:
                        uri = _b.sent();
                        documentUrl = new URL(uri);
                        _a = args.id, id = _a === void 0 ? uuid() : _a;
                        params = {
                            textDocument: {
                                uri: documentUrl.href,
                            },
                            command: command,
                            argument: JSON.stringify(args),
                        };
                        body = {
                            jsonrpc: '2.0',
                            id: id,
                            method: Methods.commandExecuted,
                            params: params,
                        };
                        return [4 /*yield*/, this._sendNotificationAsync(JSON.stringify(body))];
                    case 2:
                        _b.sent();
                        return [2 /*return*/];
                }
            });
        });
    };
    PowerFxLanguageClient.prototype.requestProvideCompletionItemsAsync = function (currentText, line, // starts at 0
    character, // starts at 0
    triggerKind, triggerCharacter) {
        return __awaiter(this, void 0, void 0, function () {
            var uri, documentUrl, params, body, result, noSuggestions, responseObject;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this._getDocumentUriAsync()];
                    case 1:
                        uri = _a.sent();
                        documentUrl = new URL(uri);
                        documentUrl.searchParams.set('expression', currentText);
                        params = {
                            textDocument: {
                                uri: documentUrl.href,
                            },
                            position: {
                                line: line,
                                character: character,
                            },
                            context: {
                                triggerKind: triggerKind,
                                triggerCharacter: triggerCharacter,
                            },
                        };
                        body = {
                            jsonrpc: '2.0',
                            id: uuid(),
                            method: Methods.completion,
                            params: params,
                        };
                        return [4 /*yield*/, this._sendRequestAsync(body.id, JSON.stringify(body))];
                    case 2:
                        result = _a.sent();
                        noSuggestions = { isIncomplete: false, items: [] };
                        if (!result.success || !result.response) {
                            return [2 /*return*/, noSuggestions];
                        }
                        responseObject = JSON.parse(result.response);
                        if (!responseObject || !responseObject.result || !Array.isArray(responseObject.result.items)) {
                            return [2 /*return*/, noSuggestions];
                        }
                        return [2 /*return*/, responseObject.result];
                }
            });
        });
    };
    PowerFxLanguageClient.prototype.requestInitialFixupAsync = function (text) {
        return __awaiter(this, void 0, void 0, function () {
            var uri, params, body, result, responseObject;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this._getDocumentUriAsync()];
                    case 1:
                        uri = _a.sent();
                        params = {
                            textDocument: {
                                uri: uri,
                                languageId: languageName,
                                version: 0,
                                text: text,
                            },
                        };
                        body = {
                            jsonrpc: '2.0',
                            id: uuid(),
                            method: Methods.initialFixup,
                            params: params,
                        };
                        return [4 /*yield*/, this._sendRequestAsync(body.id, JSON.stringify(body))];
                    case 2:
                        result = _a.sent();
                        if (!result.success || !result.response) {
                            return [2 /*return*/, text];
                        }
                        responseObject = JSON.parse(result.response);
                        if (!responseObject || !responseObject.result || typeof responseObject.result.text !== 'string') {
                            return [2 /*return*/, text];
                        }
                        return [2 /*return*/, responseObject.result.text];
                }
            });
        });
    };
    PowerFxLanguageClient.prototype.requestProvideSignatureHelpAsync = function (currentText, line, // starts at 0
    character // starts at 0
    ) {
        return __awaiter(this, void 0, void 0, function () {
            var uri, documentUrl, params, body, result, noSignatureHelp, responseObject;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this._getDocumentUriAsync()];
                    case 1:
                        uri = _a.sent();
                        documentUrl = new URL(uri);
                        documentUrl.searchParams.set('expression', currentText);
                        params = {
                            textDocument: {
                                uri: documentUrl.href,
                            },
                            position: {
                                line: line,
                                character: character,
                            },
                        };
                        body = {
                            jsonrpc: '2.0',
                            id: uuid(),
                            method: Methods.signatureHelp,
                            params: params,
                        };
                        return [4 /*yield*/, this._sendRequestAsync(body.id, JSON.stringify(body))];
                    case 2:
                        result = _a.sent();
                        noSignatureHelp = { signatures: [], activeSignature: null, activeParameter: null };
                        if (!result.success || !result.response) {
                            return [2 /*return*/, noSignatureHelp];
                        }
                        responseObject = JSON.parse(result.response);
                        if (!responseObject || !responseObject.result || !Array.isArray(responseObject.result.signatures)) {
                            return [2 /*return*/, noSignatureHelp];
                        }
                        return [2 /*return*/, responseObject.result];
                }
            });
        });
    };
    PowerFxLanguageClient.prototype.requestCodeActionAsync = function (currentText, range, kind) {
        return __awaiter(this, void 0, void 0, function () {
            var uri, documentUrl, params, id, body, result, noCodeActions, responseObject;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this._getDocumentUriAsync()];
                    case 1:
                        uri = _a.sent();
                        documentUrl = new URL(uri);
                        documentUrl.searchParams.set('expression', currentText);
                        params = {
                            textDocument: {
                                uri: documentUrl.href,
                            },
                            range: range,
                            context: {
                                diagnostics: [],
                                only: [kind],
                            },
                        };
                        id = uuid();
                        body = {
                            jsonrpc: '2.0',
                            id: id,
                            method: Methods.codeActions,
                            params: params,
                        };
                        return [4 /*yield*/, this._sendRequestAsync(body.id, JSON.stringify(body))];
                    case 2:
                        result = _a.sent();
                        noCodeActions = [];
                        if (!(result === null || result === void 0 ? void 0 : result.success) || !(result === null || result === void 0 ? void 0 : result.response)) {
                            return [2 /*return*/, { actions: noCodeActions }];
                        }
                        responseObject = JSON.parse(result.response);
                        if (!Array.isArray(responseObject === null || responseObject === void 0 ? void 0 : responseObject.result[kind])) {
                            return [2 /*return*/, { actions: noCodeActions }];
                        }
                        return [2 /*return*/, { id: id, uri: documentUrl.href, actions: responseObject.result[kind] }];
                }
            });
        });
    };
    PowerFxLanguageClient.prototype.onDataReceivedFromLanguageServer = function (payload) {
        var _this = this;
        try {
            var payloadObject = JSON.parse(payload);
            var responseArray = Array.isArray(payloadObject) ? payloadObject : [payload];
            responseArray.forEach(function (responsePayload) {
                var response = JSON.parse(responsePayload);
                // Handle publishDiagnostics notification
                if (response.method === Methods.publishDiagnostics) {
                    _this._handleDiagnosticsNotification(response.params);
                    return;
                }
                // No action required for code actions in this block.
                if (response.method === Methods.codeActions) {
                    return;
                }
                // Handle publishTokens notification
                if (response.method === Methods.publishTokens) {
                    _this._handleTokensNotification(response.params);
                    return;
                }
                // Handle response with id
                if (response.id) {
                    _this._pendingPromises.forEach(function (item) {
                        if (item.id === response.id) {
                            item.resolve(JSON.stringify(response));
                        }
                    });
                }
            });
        }
        catch (error) {
            // eslint-disable-next-line no-console
            console.warn(error);
        }
    };
    return PowerFxLanguageClient;
}());
export { PowerFxLanguageClient };
//# sourceMappingURL=PowerFxLanguageClient.js.map